import React, { useState, useEffect, useMemo } from "react";
import {
  getUserProjects,
  getAvailableProjectsForUser,
  assignProjectsToUser,
  removeProjectsFromUser
} from "../api";

const UserProjectAssignmentModal = ({ open, onClose, user, onSuccess }) => {
  const [allProjects, setAllProjects] = useState([]);
  const [assignedProjects, setAssignedProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [companyInfo, setCompanyInfo] = useState(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Track selections
  const [selectedProjects, setSelectedProjects] = useState(new Set());

  useEffect(() => {
    if (open && user) {
      fetchData();
    }
  }, [open, user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");
      
      const [projectsRes, assignedRes] = await Promise.all([
        getAvailableProjectsForUser(user.id), // Get projects from user's company only
        getUserProjects(user.id)
      ]);
      
      // Extract projects array and company info from response
      const responseData = projectsRes?.data || projectsRes || {};
      const projectsData = responseData.projects || [];
      setAllProjects(projectsData);
      setCompanyInfo({
        company_id: responseData.company_id,
        total: responseData.total
      });
      
      const assignments = assignedRes?.data || assignedRes || [];
      setAssignedProjects(assignments);
      
      // Initialize selected projects
      const assignedSet = new Set(assignments.map(a => a.project_id));
      setSelectedProjects(assignedSet);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError(err?.message || "Failed to load project data");
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = useMemo(() => {
    let result = allProjects;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p =>
        p.name?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query) ||
        p.group_name?.toLowerCase().includes(query)
      );
    }

    // Type filter
    if (typeFilter !== "all") {
      result = result.filter(p => p.type === typeFilter);
    }

    // Status filter
    if (statusFilter !== "all") {
      result = result.filter(p => String(p.status) === statusFilter);
    }

    // Sort by name
    result.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

    return result;
  }, [allProjects, searchQuery, typeFilter, statusFilter]);

  const toggleProject = (projectId) => {
    const newSelected = new Set(selectedProjects);
    if (newSelected.has(projectId)) {
      newSelected.delete(projectId);
    } else {
      newSelected.add(projectId);
    }
    setSelectedProjects(newSelected);
  };

  const selectAll = () => {
    const allProjectIds = new Set(filteredProjects.map(p => p.id));
    setSelectedProjects(allProjectIds);
  };

  const deselectAll = () => {
    setSelectedProjects(new Set());
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      
      // Find projects to add
      const currentlyAssigned = new Set(assignedProjects.map(a => a.project_id));
      const toAdd = Array.from(selectedProjects).filter(id => !currentlyAssigned.has(id));
      
      // Find projects to remove
      const toRemove = Array.from(currentlyAssigned).filter(id => !selectedProjects.has(id));

      // Execute operations
      const promises = [];
      
      if (toAdd.length > 0) {
        // Batch assign with default 'view' access
        promises.push(
          assignProjectsToUser(user.id, toAdd, 'view', 'Assigned via UI')
        );
      }
      
      if (toRemove.length > 0) {
        promises.push(
          removeProjectsFromUser(user.id, toRemove)
        );
      }

      await Promise.all(promises);
      
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Failed to save assignments:', err);
      setError(err?.message || 'Failed to save project assignments');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b dark:border-gray-800">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Manage Projects for {user?.first_name || user?.display_name || user?.email}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {selectedProjects.size} of {allProjects.length} project(s) selected
                {companyInfo?.company_id && (
                  <span className="ml-2 text-xs">
                    • Showing projects from user's company
                  </span>
                )}
                {!companyInfo?.company_id && !loading && (
                  <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">
                    • User has no company assigned
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="p-6 border-b dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search projects..."
                className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
              />
              <svg
                className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.3-4.3" />
              </svg>
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="border dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
            >
              <option value="all">All Types</option>
              <option value="chat">Chat</option>
              <option value="voice">Voice</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
            >
              <option value="all">All Status</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>
          
          {/* Select All / Deselect All Buttons */}
          {filteredProjects.length > 0 && (
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="px-3 py-1.5 text-sm bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors font-medium"
              >
                Select All ({filteredProjects.length})
              </button>
              <button
                onClick={deselectAll}
                className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-medium"
              >
                Deselect All
              </button>
            </div>
          )}
        </div>

        {/* Project List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-[#32B1FF] border-r-transparent"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">Loading projects...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-600 dark:text-red-400 mb-2">⚠️ {error}</div>
              <button
                onClick={fetchData}
                className="px-4 py-2 bg-[#32B1FF] text-white rounded-lg hover:bg-[#259ae6] transition-colors"
              >
                Retry
              </button>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <svg
                className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-700"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                viewBox="0 0 24 24"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.3-4.3" />
              </svg>
              <p className="font-semibold">No projects found</p>
              <p className="text-sm mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredProjects.map((project) => {
                const isSelected = selectedProjects.has(project.id);
                
                return (
                  <div
                    key={project.id}
                    className={`border dark:border-gray-700 rounded-lg p-4 transition-colors cursor-pointer ${
                      isSelected
                        ? 'border-[#32B1FF] bg-blue-50 dark:bg-blue-900/20'
                        : 'hover:border-gray-400 dark:hover:border-gray-600'
                    }`}
                    onClick={() => toggleProject(project.id)}
                  >
                    <div className="flex items-start gap-4">
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleProject(project.id)}
                        className="mt-1 w-5 h-5 text-[#32B1FF] border-gray-300 rounded focus:ring-[#32B1FF] cursor-pointer"
                        onClick={(e) => e.stopPropagation()}
                      />
                      
                      {/* Project Logo */}
                      <img
                        src={project.logoUrl || "/Rigsby.png"}
                        alt={project.name}
                        className="w-12 h-12 rounded-lg object-cover border dark:border-gray-700 flex-shrink-0"
                        onError={(e) => {
                          e.target.src = "/Rigsby.png";
                        }}
                      />
                      
                      {/* Project Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                            {project.name}
                          </h3>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                            project.type === "chat"
                              ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                              : "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
                          }`}>
                            {project.type}
                          </span>
                          {project.ai_enabled && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded bg-gradient-to-r from-[#32B1FF]/10 to-[#2A9FEB]/10 text-[#32B1FF] border border-[#32B1FF]/20">
                              AI
                            </span>
                          )}
                        </div>
                        {project.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {project.description}
                          </p>
                        )}
                        {project.group_name && (
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            Group: {project.group_name}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-300 text-sm">
              {error}
            </div>
          )}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={saving}
              className="flex-1 px-4 py-2 border dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="flex-1 px-4 py-2 bg-[#32B1FF] text-white rounded-lg hover:bg-[#259ae6] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
            >
              {saving ? "Saving..." : "Save Assignments"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProjectAssignmentModal;

